import { hashCode } from './hash-code'
import { prune } from './prune'
import { shortId } from './short-id'

/**
 * Common magic numbers live here.
 */
export enum Num {
    secToMs = 1000
}

/**
 * Helper functions.
 */
export const Utils = {
    /**
     * De-camelCase a string.
     *
     * @param {string} str
     * @param {string} separator - optional. Space is used if not provided
     * @returns {string}
     */
    deCamelCase(str: string, separator = ' '): string {
        if (str) {
            return str
                .replace(/([a-z\d])([A-Z])/g, '$1' + separator + '$2')
                .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, '$1' + separator + '$2')
        }
        return undefined
    },

    /**
     * Creates new object with de-camelcased keys (non-recursive).
     *
     * @param {object} obj to convert keys
     * @returns {object}
     */
    deCamelCaseKeys(obj: any): object {
        if (obj) {
            const out: any = {}
            Object.keys(obj).forEach((key) => out[this.deCamelCase(key)] = obj[key])
            return out
        }
        return undefined
    },

    /**
     * Helper function to decode special characters
     *
     * @param {string}
     * @returns {string}
     */

    decodeHtmlEntity(str: string): string {
        const domParserHtmlEntities = new DOMParser()
        return domParserHtmlEntities.parseFromString(str, 'text/html').body.textContent
    },

    hashCode,

    /**
     * Check if an object is empty or not
     *
     * @param {object} obj
     * @returns {boolean}
     */
    isEmptyObject(obj: any): boolean {
        return !Object.entries(obj).length
    },

    /**
     * Creates new object with string properties (non-recursive).
     *
     * @param {object} obj
     * @returns {object} with string properties
     */
    objectValuesToStrings(obj: any): object {
        if (obj) {
            const out: any = {}
            Object.keys(obj).forEach((key) => out[key] = String(obj[key]))
            return out
        }
        return undefined
    },

    /**
     * Races supplied promise with a timeout rejection.
     *
     * @param promise
     * @param millis
     */
    async promiseTimeout<T>(promise: Promise<T>, millis = Num.secToMs): Promise<T> {
        return Promise.race([
            promise,
            new Promise<T>((_, reject) =>
                setTimeout(() => reject(new Error(`Promise timed out after ${millis} ms.`)), millis))
        ])
    },

    prune,

    /**
     * Retries a predicate until it returns true.
     *
     * @param predicate
     * @param times
     * @param sleep
     */
    async retry(predicate: () => Promise<boolean>, times = 3, sleep = 1000, timeout = 1000) {
        return new Promise(async (resolve, reject) => {
            for (let i = 0; i < times; i += 1) {
                const finished = await new Promise(async (resolveSub, rejectSub) => {
                    const res = await this.promiseTimeout(predicate(), timeout).catch(rejectSub)
                    if (!res && i < times - 1) {
                        setTimeout(resolveSub, sleep)
                    } else {
                        resolveSub(res)
                    }
                }).catch(console.log)

                if (finished) {
                    resolve()
                    break
                }
            }
            reject()
        })
    },

    shortId
}
